import React, { useEffect, useCallback, useState } from "react"
import { createPortal } from "react-dom"
import { Phone, PhoneSlash, MapPin, Buildings, Clock } from "@phosphor-icons/react"
import { cn } from "../../lib/utils"
import { useCallStore, QueueEntry } from "../../stores"
import { dialNumber, playSound } from "../../lib/twilio"
import { showIncomingCallAlert, dismissIncomingCallAlert } from "../../lib/twilio/incoming-call-alerts"
import { api } from "../../lib/api/client"
import { getSocket } from "../../lib/socket"

// Format phone number for display
function formatPhone(phone: string): string {
  if (!phone) return ""
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

export function QueueModal() {
  const {
    browserDialing,
    incomingCall,
    currentCall,
    queueEntry,
    isInQueue,
    setQueueEntry,
  } = useCallStore()

  const [cancelled, setCancelled] = useState<string | null>(null)

  const canAccept =
    browserDialing === "ready" &&
    isInQueue &&
    !currentCall &&
    !incomingCall



  const getCityAndState = useCallback((entry: QueueEntry) => {
    let str = ""
    if (entry.contact.city) str = entry.contact.city
    if (entry.contact.state) {
      str = str ? str + ", " + entry.contact.state : entry.contact.state
    }
    return str || "Unknown Location"
  }, [])

  const handleAccept = useCallback(() => {
    if (!queueEntry) return
    dismissIncomingCallAlert()
    setCancelled(queueEntry._id)
    setQueueEntry(null)
    dialNumber({
      name: queueEntry.contact.display_name || "Unknown Caller",
      address: getCityAndState(queueEntry),
      number: queueEntry.contact.phone,
      contact: queueEntry.contact._id,
      division: queueEntry.division._id,
      matter: queueEntry.matter,
      call_queue_entry: queueEntry._id,
    })
  }, [queueEntry, getCityAndState, setQueueEntry])

  const handleDecline = useCallback(async () => {
    if (!queueEntry) return
    dismissIncomingCallAlert()
    setCancelled(queueEntry._id)
    try {
      await api.callOffers.decline(queueEntry.call || queueEntry._id)
    } catch (e) {
      console.error("Failed to decline queue entry:", e)
    }
  }, [queueEntry])

  // Check for queue entries (skip if a direct dial is already ringing)
  const checkQueue = useCallback(async () => {

    if (!canAccept || incomingCall) return
    try {
      const data = await api.callQueueEntries.check()

      if (data.data && canAccept && !incomingCall) {

        setQueueEntry(data.data)
        playSound()
        showIncomingCallAlert(
          data.data.contact?.display_name || "",
          data.data.contact?.phone || "",
        )
      }
    } catch (e) {
      console.error("Failed to check queue:", e)
    }
  }, [canAccept, incomingCall, setQueueEntry])

  // Play ringtone while queue is shown (skip if a direct dial is already ringing)
  useEffect(() => {

    if (!canAccept || !queueEntry || queueEntry._id === cancelled || incomingCall) return


    const interval = setInterval(() => {
      playSound()
    }, 2500)

    return () => {
      clearInterval(interval)
    }
  }, [canAccept, queueEntry, cancelled, incomingCall])

  // Listen for socket events
  useEffect(() => {
    const socket = getSocket()
    if (!socket) return

    const onQueueCreated = () => {
      if (canAccept) checkQueue()
    }

    const onQueueTaken = (takenQueue: { _id: string }) => {
      if (queueEntry && queueEntry._id === takenQueue._id) {
        dismissIncomingCallAlert()
        setQueueEntry(null)
        checkQueue()
      }
    }

    const onQueueDeleted = (deletedQueue: { _id: string }) => {
      if (queueEntry && queueEntry._id === deletedQueue._id) {
        dismissIncomingCallAlert()
        setQueueEntry(null)
        checkQueue()
      }
    }

    socket.on("CALL_CENTER.CALL_QUEUE_CREATED", onQueueCreated)
    socket.on("CALL_CENTER.CALL_QUEUE_TAKEN", onQueueTaken)
    socket.on("CALL_CENTER.CALL_QUEUE_DELETED", onQueueDeleted)

    return () => {
      socket.off("CALL_CENTER.CALL_QUEUE_CREATED", onQueueCreated)
      socket.off("CALL_CENTER.CALL_QUEUE_TAKEN", onQueueTaken)
      socket.off("CALL_CENTER.CALL_QUEUE_DELETED", onQueueDeleted)
    }
  }, [canAccept, queueEntry, checkQueue, setQueueEntry])

  // Initial check when can accept changes
  useEffect(() => {
    if (canAccept) checkQueue()
  }, [canAccept, checkQueue])

  // Clear cancelled when can't accept
  useEffect(() => {
    if (!canAccept) setCancelled(null)
  }, [canAccept])

  // Don't render if no queue or cancelled
  if (!canAccept || !queueEntry || queueEntry._id === cancelled) {

    return null
  }



  const modalContent = (
    <div className="fixed inset-0 z-[99997] flex items-center justify-center pointer-events-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative queue-modal-container animate-queue-appear">
        {/* Pulsing border effect */}
        <div className="absolute inset-0 rounded-2xl queue-pulse-border" />

        <div className="relative bg-sidebar-dark rounded-2xl overflow-hidden shadow-2xl min-w-[320px]">
          {/* Header */}
          <div
            className={cn(
              "px-6 py-4",
              queueEntry.queue_entry_color
                ? ""
                : "bg-gradient-to-r from-primary to-primary/80"
            )}
            style={
              queueEntry.queue_entry_color
                ? { background: queueEntry.queue_entry_color }
                : undefined
            }
          >
            <h3 className="text-lg font-bold text-white text-center">
              {queueEntry.queue_entry_title || "Incoming Queue Call"}
            </h3>
          </div>

          {/* Body */}
          <div className="px-6 py-5 bg-white">
            <h2 className="text-xl font-bold text-gray-900 text-center">
              {queueEntry.contact.display_name
                ? queueEntry.contact.display_name.length > 25
                  ? queueEntry.contact.display_name.slice(0, 25) + "..."
                  : queueEntry.contact.display_name
                : "Unknown Caller"}
            </h2>
            <p className="mt-1 text-sm font-semibold text-gray-600 text-center">
              {formatPhone(queueEntry.contact.phone)}
            </p>
            <div className="mt-1 flex items-center justify-center gap-1 text-sm text-gray-500">
              <MapPin className="h-3 w-3" />
              <span>{getCityAndState(queueEntry)}</span>
            </div>
          </div>

          {/* Info */}
          <div className="px-6 py-3 bg-gray-100 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Buildings className="h-3.5 w-3.5" />
              <span className="font-medium">Division:</span>
              <span>{queueEntry.division.name}</span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-medium">Type:</span>
              <span>
                {queueEntry.type === "hold" ? "On Hold" : "Callback Requested"}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex gap-3">
              <button
                onClick={handleDecline}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl",
                  "bg-danger hover:bg-danger/90 transition-colors",
                  "text-sm font-bold text-white shadow-lg",
                  "hover:shadow-danger/30 hover:shadow-xl"
                )}
              >
                <PhoneSlash className="h-4 w-4" />
                <span>Decline</span>
              </button>
              <button
                onClick={handleAccept}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl",
                  "bg-success hover:bg-success/90 transition-colors",
                  "text-sm font-bold text-white shadow-lg",
                  "hover:shadow-success/30 hover:shadow-xl"
                )}
              >
                <Phone className="h-4 w-4" />
                <span>Accept</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
